import type { ResourceState } from '@/common/types';
import type { ReorderableItem } from '@/common/ui';
import type {
  EpochData,
  ScoreSetEvent,
  ScoreSetEventsData,
  ShortcutEntry,
  SignalConfig,
  SignalOverflow
} from '@/modules/epoch/types';
import { PreferredView } from '@/modules/user/types';

export enum ViewMode {
  Single = 'single',
  Dual = 'dual'
}

export interface DualTimeBaseSettings {
  topWindowSize: number;
  bottomWindowSize: number;
  singleHypnogram?: boolean;
}

export interface DialogSettings {
  visibility: boolean;
}

export interface SignalEvent extends ScoreSetEvent {
  eventType: string;
  eventIndex?: number;
  isRespInWakeUnstaged?: boolean;
}

export interface SettingPreset {
  id: number;
  name: string;
  signalsList: ReorderableItem[];
  signalOverflow: SignalOverflow;
  band: boolean;
  notch: boolean;
  fullNightSignals: string[];
  hypnogramFullNightSignals: ReorderableItem[];
  viewMode?: ViewMode;
  dualTimeBase?: DualTimeBaseSettings;
  artifactColor?: string;
  shortcutKeys?: ShortcutEntry[];
}

export interface LocalStateSettings {
  presets: SettingPreset[];
  selectedPresetId: number;
  preferredView?: PreferredView;
}

export const LOCAL_STATE_KEY = 'signal-settings';

export interface EpochState {
  fullNight: ResourceState<EpochData>;
  epochData: ResourceState<EpochData>;
  granularOffsetSec: number;
  // Offset to apply on the NEXT epoch transition. Used by the wheel handler to
  // hand a non-zero leftover to the post-route-change watcher without ever
  // committing it against the *old* route epoch (which would briefly render an
  // effectiveEpoch on the wrong epoch and cause a visible jump-and-snap glitch
  // at boundary crossings during granular wheel navigation).
  pendingGranularOffsetSec: number;
  graph: {
    zoom: {
      [key: string]: SignalConfig;
    };
    overflowVisible: {
      [key: string]: boolean;
    };
    polarityInversion: {
      [key: string]: boolean;
    };
  };
  dialog: DialogSettings;
  events: ResourceState<ScoreSetEventsData>;
  eventActions: {
    isLoading?: boolean;
    errorTimestamp?: number;
  };
  selectedScoreSetId?: string;
  signalEvents: {
    [signal: string]: SignalEvent[];
  };
  signalEventMapping: {
    [signal: string]: string[];
  };
  overlappedRespiratoryEvents: ScoreSetEvent[];
  settings: {
    isLoading?: boolean;
    signalsList: ReorderableItem[];
    signalOverflow: SignalOverflow;
    presets: SettingPreset[];
    selectedPresetId: number;
    hypnogramFullNightSignals: ReorderableItem[];
    band: boolean;
    notch: boolean;
    viewMode: ViewMode;
    dualTimeBase: DualTimeBaseSettings;
    artifactColor: string;
    shortcutKeys: ShortcutEntry[];
  };
  initialAutoDefaults: {
    [signal: string]: {
      min: number;
      max: number;
    };
  };
}
